<svelte:options customElement={{ tag: "bt-tabs", shadow: "none", }} />

<script lang="ts">
	import { onMount } from 'svelte';
	import { eventBus, type Events } from '$shared/lib/eventBus';

	export let label = '';
	export let labelledById = '';
	export let id;
	export let tabHelpTextId;
	export let tabHelpText = '';
	export let design: 'grid' | 'default' = 'default';

	$: labelAsId = label.replaceAll(' ', '').toLowerCase();

	let tabs: any;
	let panels: any;

	let selectedPanelId: string | null;

	const selectPanelByIndex = (allTabs: Element[], allPanels: Element[], index: number) => {
		allTabs[index].setAttribute('aria-selected', 'true');
		allTabs[index].classList.add('is-selected');
		allPanels[index].classList.remove('hidden');
		if (tabHelpTextId) {
			let helpTextElActual = document.querySelector(`#${tabHelpTextId}`);
			let helpText = '';
			// Will need to refactor if we ever need help text for > 2 tabs
			if (tabs.length === 1) {
				for (let someTab of allTabs) {
					if (someTab.getAttribute('aria-controls') == allPanels[index].id) {
						helpText = $$restProps[`${someTab.dataset.tabHelptextAttr}`]
					}
				}
			} else {
				helpText = $$restProps[`tab${index+1}text`];
			}
			if (!helpTextElActual) return;
			if (helpText) {
				helpTextElActual.textContent = helpText;
			} else {
				helpTextElActual.textContent = '';
			}
		}
	}

	const unselectPanelByIndex = (allTabs: Element[], allPanels: Element[], index: number) => {
		allTabs[index].removeAttribute('aria-selected');
		allTabs[index].classList.remove('is-selected');
		allPanels[index].classList.add('hidden');
	}

	const changeTabToPanelId = (targetPanelId) => {
		// Return early for unregistered panels etc
		if (!selectedPanelId || !targetPanelId) return;
		if (selectedPanelId === targetPanelId) return;
		const indexOfTargetPanel = [...panels].findIndex((panel: any) => {
			return panel.getAttribute('id') === targetPanelId;
		});
		if (indexOfTargetPanel < 0) return;
		for (let i = 0; i < panels.length; i++) {
			if (i === indexOfTargetPanel) {
				selectPanelByIndex(tabs, panels, i);
			}
			else {
				unselectPanelByIndex(tabs, panels, i);
			}
		}
		selectedPanelId = targetPanelId;
	}

	onMount(() => {
		tabs = document.querySelector(`#${id} [slot="tabs"]`)?.children;
		panels = document.querySelector(`#${id} [slot="panels"]`)?.children;

		if (tabs?.length) {
			tabs[0].setAttribute('aria-selected', 'true');
			// Register handlers
			for (const tab of tabs) {
				tab.addEventListener('click', (event: CustomEvent) => {
					changeTabToPanelId(event?.target?.getAttribute('aria-controls'));
				});
			};
		}

		if (panels?.length) {
			selectedPanelId = panels[0].getAttribute('id');
			for (let i = 0; i < panels.length; i++) {
				if (i === 0) {
					selectPanelByIndex(tabs, panels, i);
				}
				else {
					unselectPanelByIndex(tabs, panels, i);
				}
			}
		}
		eventBus.on('tab-change', event => {
			changeTabToPanelId(event.targetTabId);
		});
		return () => {
			eventBus.off('tab-change');
		}
	});
</script>

<section class={design === 'grid' ? 'tabs-and-panels  tabs-and-panels--grid' : 'tabs-and-panels'} id={id}>

	{#if label}
		<span class="screen-reader-only" id={`${labelAsId}-tab-label`}>{label}</span>
	{/if}

	<div 
		role="tablist" 
		aria-labelledby={label ? `${labelAsId}-tab-label` : labelledById}
	>
		<slot name="tabs"></slot>
	</div>

	<div>
			<slot name="panels"></slot>
	</div>

</section>

<style>
	.screen-reader-only {
		clip: rect(0 0 0 0); 
	  clip-path: inset(50%);
	  height: 1px;
	  overflow: hidden;
	  position: absolute;
	  white-space: nowrap; 
	  width: 1px;
	}
	:global(bt-tabs .tab), :global(.tabs-and-panels .tab) {
		border: 1px solid transparent;
		border-bottom: none;
		color: theme('colors.neutral.550');
		font-weight: theme('fontWeight.medium');
		padding: theme('spacing.2');
		position: relative;
		&:hover {
			cursor: pointer;
			color: theme('colors.neutral.900');

		}
	}
	:global(bt-tabs .tab.is-selected), :global(.tabs-and-panels .tab.is-selected) {
		border-color: theme('colors.neutral.900');
		color: theme('colors.neutral.900');
		font-weight: theme('fontWeight.semibold');
	}
	:global(bt-tabs [slot="tabs"]), :global(.tabs-and-panels [slot="tabs"]) {
		display: block;
		position: relative;
		&:after {
			content: '';
			display: block;
			width: 100%;
			height: 1px;
			background-color: theme('colors.neutral.900');
			position: absolute;
			bottom: 0;
		}
	}

	:global(bt-tabs:has(.tabs-and-panels--grid)) {

	}
</style>